// Excel import/export formats (PLAN.md §2C, E01-E11). Pure functions over rows so they can be unit tested.
import type { Cell, OutSheet } from "./xlsx";
import { xlDate } from "./xlsx";
import { parseCum, formatCum } from "../engine/chains";
import { addDays, parseDate } from "../engine/dates";
import type { Breakdown, Chain, Codes, Period } from "../engine/types";
import { BREAKDOWNS } from "../engine/types";

export const norm = (v: Cell) => String(v ?? "").replace(/\s+/g, " ").trim().toUpperCase();
const str = (v: Cell) => (v === null || v === undefined ? "" : v instanceof Date ? v.toISOString().slice(0, 10) : String(v).trim());
const num = (v: Cell): number | null => {
  if (v === null || v === "" || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return isFinite(n) ? n : null;
};
const truthy = (v: Cell) => ["Y", "YES", "TRUE", "X", "1"].includes(norm(v)) || v === true;

export interface RowError {
  line: number; // 1-based sheet row
  message: string;
}

/** Find the first row that contains all the given headers (any alias). */
export function findHeader(rows: Cell[][], groups: string[][], scan = 30): { row: number; col: (g: number) => number } | null {
  for (let r = 0; r < Math.min(scan, rows.length); r++) {
    const cells = (rows[r] ?? []).map(norm);
    const cols = groups.map((aliases) => cells.findIndex((c) => aliases.includes(c)));
    if (cols.every((c) => c >= 0)) return { row: r, col: (g) => cols[g] };
  }
  return null;
}

function colOf(rows: Cell[][], headerRow: number, aliases: string[]) {
  return (rows[headerRow] ?? []).map(norm).findIndex((c) => aliases.includes(c));
}

// ---------- Breakdowns (E01) ----------

export interface NodeRow {
  code: string;
  title: string;
  parentCode?: string;
  dbsType?: "discipline" | "procurement";
  order?: number;
}

export function parseBreakdown(rows: Cell[][], type: Breakdown): { nodes: NodeRow[]; errors: RowError[] } {
  const h = findHeader(rows, [["CODE", type.toUpperCase(), `${type.toUpperCase()} CODE`], ["DESCRIPTION", "TITLE", "NAME"]]);
  if (!h) return { nodes: [], errors: [{ line: 1, message: "Couldn't find the Code and Description headers" }] };
  const pc = colOf(rows, h.row, ["PARENT", "PARENT CODE"]);
  const tc = colOf(rows, h.row, ["TYPE", "DBS TYPE"]);
  const nodes: NodeRow[] = [];
  const errors: RowError[] = [];
  const seen = new Map<string, number>();
  for (let r = h.row + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const code = str(row[h.col(0)]);
    const title = str(row[h.col(1)]);
    if (!code && !title) continue;
    if (!code) {
      errors.push({ line: r + 1, message: "Missing code" });
      continue;
    }
    if (seen.has(code)) errors.push({ line: r + 1, message: `Code ${code} repeats row ${seen.get(code)} (Excel shows ${code} twice)` });
    seen.set(code, r + 1);
    const n: NodeRow = { code, title };
    if (pc >= 0 && str(row[pc])) n.parentCode = str(row[pc]);
    if (type === "dbs") n.dbsType = tc >= 0 && norm(row[tc]).startsWith("PROC") ? "procurement" : "discipline";
    nodes.push(n);
  }
  return { nodes, errors };
}

export function exportBreakdown(type: Breakdown, nodes: NodeRow[]): OutSheet {
  const head = ["Code", "Title", "Parent", ...(type === "dbs" ? ["Type"] : []), "Order"];
  return {
    name: type.toUpperCase(),
    headerRow: 0,
    rows: [head, ...nodes.map((n) => [n.code, n.title, n.parentCode ?? null, ...(type === "dbs" ? [n.dbsType === "procurement" ? "Procurement" : "Discipline"] : []), n.order ?? null])],
  };
}

// ---------- Milestones (E02) ----------

export function parseMilestones(rows: Cell[][]): { items: { code: string; title: string; active?: boolean }[]; errors: RowError[] } {
  const h = findHeader(rows, [["CODE"], ["MILESTONE TITLE", "TITLE", "DESCRIPTION"]]);
  if (!h) return { items: [], errors: [{ line: 1, message: "Couldn't find the Code and Title headers" }] };
  const ac = colOf(rows, h.row, ["ACTIVE"]);
  const items: { code: string; title: string; active?: boolean }[] = [];
  const errors: RowError[] = [];
  for (let r = h.row + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const code = str(row[h.col(0)]).toUpperCase();
    const title = str(row[h.col(1)]);
    if (!code && !title) continue;
    if (!code) {
      errors.push({ line: r + 1, message: "Missing code" });
      continue;
    }
    items.push({ code, title, ...(ac >= 0 && str(row[ac]) ? { active: truthy(row[ac]) } : {}) });
  }
  return { items, errors };
}

// ---------- Chains (E03): Cesar's block layout, our wide layout, or a long layout ----------

export interface ChainRow {
  code: string;
  title?: string;
  category?: string;
  steps: { milestone: string; cum: number; mfgCum?: number }[];
}

export function parseChains(rows: Cell[][]): { chains: ChainRow[]; errors: RowError[] } {
  const errors: RowError[] = [];
  // Wide: CODE, TITLE?, CATEGORY?, MS1.., VAL1..
  const wide = findHeader(rows, [["CODE", "CHAIN", "CHAIN CODE"], ["MS1", "MILESTONE1"], ["VAL1", "CUM1", "CUMULATIVE1"]]);
  if (wide) {
    const head = (rows[wide.row] ?? []).map(norm);
    const tc = head.findIndex((c) => c === "TITLE");
    const cc = head.findIndex((c) => c === "CATEGORY");
    const msCols: number[] = [];
    const valCols: number[] = [];
    head.forEach((c, i) => {
      const m = c.match(/^(?:MS|MILESTONE)\s*(\d+)$/);
      const v = c.match(/^(?:VAL|CUM|CUMULATIVE)\s*(\d+)$/);
      if (m) msCols[Number(m[1])] = i;
      if (v) valCols[Number(v[1])] = i;
    });
    const chains: ChainRow[] = [];
    for (let r = wide.row + 1; r < rows.length; r++) {
      const row = rows[r] ?? [];
      const code = str(row[wide.col(0)]).toUpperCase();
      if (!code) continue;
      const steps: ChainRow["steps"] = [];
      for (let n = 1; n < msCols.length; n++) {
        if (msCols[n] === undefined) continue;
        const ms = str(row[msCols[n]]).toUpperCase();
        if (!ms) continue;
        const cum = parseCum(row[valCols[n]] ?? null);
        if (!cum) errors.push({ line: r + 1, message: `${code}: step ${n} (${ms}) has no valid %` });
        else steps.push({ milestone: ms, ...cum });
      }
      chains.push({ code, title: tc >= 0 ? str(row[tc]) || undefined : undefined, category: cc >= 0 ? str(row[cc]) || undefined : undefined, steps });
    }
    return { chains, errors };
  }
  // Long: CODE, SEQ?, MILESTONE, CUM
  const long = findHeader(rows, [["CODE", "CHAIN", "CHAIN CODE"], ["MILESTONE"], ["CUM", "CUMULATIVE", "PERCENT", "%"]]);
  if (long) {
    const by = new Map<string, ChainRow>();
    for (let r = long.row + 1; r < rows.length; r++) {
      const row = rows[r] ?? [];
      const code = str(row[long.col(0)]).toUpperCase();
      const ms = str(row[long.col(1)]).toUpperCase();
      if (!code || !ms) continue;
      const cum = parseCum(row[long.col(2)]);
      if (!cum) {
        errors.push({ line: r + 1, message: `${code}: ${ms} has no valid %` });
        continue;
      }
      const c = by.get(code) ?? { code, steps: [] };
      c.steps.push({ milestone: ms, ...cum });
      by.set(code, c);
    }
    return { chains: [...by.values()], errors };
  }
  // Cesar's block layout ("8. MILESTONE CHAINS DEFINITION").
  const chains: ChainRow[] = [];
  let category: string | undefined;
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const cells = row.map(str);
    const cat = cells.find((c) => /^CATEGORY:/i.test(c));
    if (cat) category = cat.replace(/^CATEGORY:\s*/i, "").trim();
    const ci = cells.findIndex((c) => /^Code:/i.test(c));
    if (ci < 0) continue;
    const code = cells[ci].replace(/^Code:\s*/i, "").trim().toUpperCase();
    const li = cells.findIndex((c, i) => i > ci && /^Milestone:?$/i.test(c));
    const cols: number[] = [];
    for (let i = (li >= 0 ? li : ci) + 1; i < cells.length; i++) if (cells[i]) cols.push(i);
    const pr = rows[r + 1] ?? [];
    const mr = rows[r + 2] ?? [];
    const hasMfg = mr.some((c) => /manufacturing/i.test(str(c)));
    const steps: ChainRow["steps"] = [];
    for (const i of cols) {
      const ms = cells[i].toUpperCase();
      const cum = parseCum(pr[i] ?? null);
      if (!cum) {
        errors.push({ line: r + 2, message: `${code}: ${ms} has no valid %` });
        continue;
      }
      const step: ChainRow["steps"][number] = { milestone: ms, cum: cum.cum };
      if (hasMfg) {
        const m = parseCum(str(mr[i]).replace(/^\[|\]$/g, ""));
        if (m) step.mfgCum = m.cum;
      }
      steps.push(step);
    }
    chains.push({ code, category, steps });
  }
  if (!chains.length) errors.push({ line: 1, message: "No chains found. Use CODE, MS1..n, VAL1..n columns or the MILESTONE CHAINS DEFINITION layout." });
  return { chains, errors };
}

export function exportChains(chains: ChainRow[]): OutSheet {
  const max = Math.max(1, ...chains.map((c) => c.steps.length));
  const head: Cell[] = ["CODE", "TITLE", "CATEGORY"];
  for (let n = 1; n <= max; n++) head.push(`MS${n}`);
  for (let n = 1; n <= max; n++) head.push(`VAL${n}`);
  return {
    name: "Chains",
    headerRow: 0,
    rows: [
      head,
      ...chains.map((c) => {
        const r: Cell[] = [c.code, c.title ?? null, c.category ?? null];
        for (let n = 0; n < max; n++) r.push(c.steps[n]?.milestone ?? null);
        for (let n = 0; n < max; n++) r.push(c.steps[n] ? formatCum(c.steps[n]) : null);
        return r;
      }),
    ],
  };
}

// ---------- Calendar (E05) ----------

export function parseCalendar(rows: Cell[][], defaultHours?: number): { periods: Period[]; errors: RowError[] } {
  const h = findHeader(rows, [["PERIOD", "NUMBER", "NO", "#"], ["CUTOFF", "CUT-OFF", "CUT-OFF DATE", "CUTOFF DATE", "WEEKEND CUTOFF", "PERIOD END"]]);
  if (!h) return { periods: [], errors: [{ line: 1, message: "Couldn't find the Period and Cutoff headers" }] };
  const sc = colOf(rows, h.row, ["START", "START DATE", "PERIOD START"]);
  const hc = colOf(rows, h.row, ["STD HOURS", "STANDARD HOURS", "STANDARD NUMBER OF WORKED HOURS", "HOURS"]);
  const mc = colOf(rows, h.row, ["MONTH END", "MONTH-END", "MONTHEND"]);
  const errors: RowError[] = [];
  const raw: (Period & { line: number; hasStart: boolean })[] = [];
  for (let r = h.row + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const n = num(row[h.col(0)]);
    const cutoff = parseDate(row[h.col(1)]);
    if (n === null && !cutoff) continue;
    if (n === null || !cutoff) {
      errors.push({ line: r + 1, message: "Needs a period number and a cutoff date" });
      continue;
    }
    const start = sc >= 0 ? parseDate(row[sc]) : null;
    const hours = hc >= 0 ? num(row[hc]) : null;
    const std = hours ?? defaultHours ?? null;
    if (std === null) errors.push({ line: r + 1, message: "No standard hours (add a column or enter a default)" });
    raw.push({ number: n, start: start ?? "", cutoff, stdHours: std ?? 0, monthEnd: mc >= 0 ? truthy(row[mc]) : false, line: r + 1, hasStart: !!start });
  }
  raw.sort((a, b) => a.number - b.number);
  // Without a Start column, each period starts the day after the previous cutoff.
  // The first period's start is only derivable if the file gives it; otherwise it mirrors the second period's length.
  raw.forEach((p, i) => {
    if (p.hasStart) return;
    if (i > 0) p.start = addDays(raw[i - 1].cutoff, 1);
    else if (raw[1]) {
      const len = Math.round((Date.parse(raw[1].cutoff) - Date.parse(p.cutoff)) / 86400000);
      p.start = addDays(p.cutoff, -(len - 1));
      errors.push({ line: p.line, message: `No start date given; period ${p.number} assumed to start ${p.start} (same length as period ${raw[1].number}). Check before saving.` });
    } else errors.push({ line: p.line, message: "No start date" });
  });
  return { periods: raw.map(({ number, start, cutoff, stdHours, monthEnd }) => ({ number, start, cutoff, stdHours, monthEnd })), errors };
}

export function exportCalendar(periods: Period[]): OutSheet {
  return {
    name: "Calendar",
    headerRow: 0,
    rows: [["Period", "Start", "Cutoff", "Std Hours", "Month End"], ...periods.map((p) => [p.number, xlDate(p.start), xlDate(p.cutoff), p.stdHours, p.monthEnd ? "Y" : null])],
  };
}

// ---------- Budget (E06) ----------

export interface BudgetRow {
  pbs?: string;
  dbs: string;
  wbs: string;
  hours: number;
}

/** Long layout (PBS?, DBS, WBS?, Hours) or Cesar's DBS table (Code, Description, Initial Budget, ...). */
export function parseBudget(rows: Cell[][]): { cells: BudgetRow[]; errors: RowError[] } {
  const errors: RowError[] = [];
  const long = findHeader(rows, [["DBS"], ["HOURS", "BUDGET", "BUDGET HOURS"]]);
  const cesar = findHeader(rows, [["CODE"], ["INITIAL BUDGET", "BUDGET"]]);
  const h = long ?? cesar;
  if (!h) return { cells: [], errors: [{ line: 1, message: "Couldn't find DBS and Hours (or Code and Initial Budget) headers" }] };
  const wc = colOf(rows, h.row, ["WBS"]);
  const pc = colOf(rows, h.row, ["PBS", "PHASE"]);
  const cells: BudgetRow[] = [];
  for (let r = h.row + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const dbs = str(row[h.col(0)]);
    const hrs = num(row[h.col(1)]);
    if (!dbs && hrs === null) continue;
    if (!dbs || hrs === null) {
      errors.push({ line: r + 1, message: "Needs a DBS code and hours" });
      continue;
    }
    cells.push({ dbs, wbs: wc >= 0 ? str(row[wc]) : "", hours: hrs, ...(pc >= 0 && str(row[pc]) ? { pbs: str(row[pc]) } : {}) });
  }
  return { cells, errors };
}

export function exportBudget(cells: BudgetRow[]): OutSheet {
  return { name: "Budget", headerRow: 0, rows: [["PBS", "DBS", "WBS", "Hours"], ...cells.map((c) => [c.pbs ?? null, c.dbs, c.wbs || null, c.hours])] };
}

// ---------- Change orders (E07) ----------

export interface CoRow {
  number: string;
  kind: "budget" | "weight";
  description?: string;
  dbs?: string;
  wbs?: string;
  pbs?: string;
  delta?: number;
  deliverableCode?: string;
  newWeight?: number;
}

/**
 * Our layout (CO NO, KIND, DESCRIPTION, DBS, WBS, PBS, DELTA, DLV REFERENCE, NEW WEIGHT) or Cesar's tbl8 table
 * (Code, Description, Initial Budget, Change Order, Revised Budget): each DBS row with a non-zero change becomes a
 * budget change order numbered `${prefix}-${code}`.
 */
export function parseChangeOrders(rows: Cell[][], prefix = "CO"): { items: CoRow[]; errors: RowError[] } {
  const errors: RowError[] = [];
  const ours = findHeader(rows, [["CO NO", "CO NUMBER", "CO", "NUMBER"], ["KIND", "TYPE"]]);
  if (ours) {
    const c = (a: string[]) => colOf(rows, ours.row, a);
    const col = { d: c(["DESCRIPTION"]), dbs: c(["DBS"]), wbs: c(["WBS"]), pbs: c(["PBS"]), delta: c(["DELTA", "CHANGE", "CHANGE HOURS", "HOURS"]), dlv: c(["DLV REFERENCE", "DELIVERABLE"]), nw: c(["NEW WEIGHT"]) };
    const items: CoRow[] = [];
    for (let r = ours.row + 1; r < rows.length; r++) {
      const row = rows[r] ?? [];
      const number = str(row[ours.col(0)]);
      if (!number) continue;
      const k = norm(row[ours.col(1)]);
      const kind = k.startsWith("W") || k.startsWith("D") ? "weight" : k.startsWith("B") ? "budget" : null;
      if (!kind) {
        errors.push({ line: r + 1, message: `${number}: KIND must be Budget or Weight` });
        continue;
      }
      const g = (i: number) => (i >= 0 ? str(row[i]) || undefined : undefined);
      const n = (i: number) => (i >= 0 ? num(row[i]) ?? undefined : undefined);
      items.push({ number, kind, description: g(col.d), dbs: g(col.dbs), wbs: g(col.wbs), pbs: g(col.pbs), delta: n(col.delta), deliverableCode: g(col.dlv), newWeight: n(col.nw) });
    }
    return { items, errors };
  }
  const tbl = findHeader(rows, [["CODE", "DBS"], ["CHANGE ORDER", "CHANGE"]]);
  if (!tbl) return { items: [], errors: [{ line: 1, message: "Couldn't find CO NO + KIND, or Code + Change Order headers" }] };
  const dc = colOf(rows, tbl.row, ["DESCRIPTION"]);
  const items: CoRow[] = [];
  for (let r = tbl.row + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const dbs = str(row[tbl.col(0)]);
    const delta = num(row[tbl.col(1)]);
    if (!dbs || !delta) continue;
    items.push({ number: `${prefix}-${dbs}`, kind: "budget", dbs, wbs: "", delta, description: dc >= 0 ? str(row[dc]) || undefined : undefined });
  }
  return { items, errors };
}

export function exportChangeOrders(cos: (CoRow & { status: string; periodNumber: number })[]): OutSheet {
  return {
    name: "Change Orders",
    headerRow: 0,
    rows: [
      ["CO NO", "KIND", "DESCRIPTION", "DBS", "WBS", "PBS", "DELTA", "DLV REFERENCE", "NEW WEIGHT", "STATUS", "EFFECTIVE PERIOD"],
      ...cos.map((c) => [c.number, c.kind === "budget" ? "Budget" : "Weight", c.description ?? null, c.dbs ?? null, c.wbs || null, c.pbs ?? null, c.delta ?? null, c.deliverableCode ?? null, c.newWeight ?? null, c.status, c.status === "approved" ? c.periodNumber : null]),
    ],
  };
}

// ---------- Expended hours (E08) ----------

export interface ActualRow {
  periodNumber: number;
  codes: Codes;
  hours: number;
  indirect: boolean;
}

/**
 * Long layout (Period, DBS, WBS?, OBS?, PBS?, Hours, Indirect?) or Cesar's wide layout: Code, Description, then one
 * column per cutoff date with periodic hours.
 */
export function parseActuals(rows: Cell[][], periods: Period[]): { entries: ActualRow[]; errors: RowError[] } {
  const errors: RowError[] = [];
  const long = findHeader(rows, [["PERIOD"], ["DBS"], ["HOURS"]]);
  if (long) {
    const col = (a: string[]) => colOf(rows, long.row, a);
    const ic = col(["INDIRECT"]);
    const codeCols = BREAKDOWNS.map((b) => [b, col([b.toUpperCase()])] as const).filter(([, c]) => c >= 0);
    const entries: ActualRow[] = [];
    for (let r = long.row + 1; r < rows.length; r++) {
      const row = rows[r] ?? [];
      const p = num(row[long.col(0)]);
      const hrs = num(row[long.col(2)]);
      if (p === null && hrs === null) continue;
      if (p === null || hrs === null) {
        errors.push({ line: r + 1, message: "Needs a period and hours" });
        continue;
      }
      if (!periods.some((x) => x.number === p)) errors.push({ line: r + 1, message: `Period ${p} is not in the calendar` });
      const codes: Codes = {};
      for (const [b, c] of codeCols) if (str(row[c])) codes[b] = str(row[c]);
      entries.push({ periodNumber: p, codes, hours: hrs, indirect: ic >= 0 ? truthy(row[ic]) : false });
    }
    return { entries, errors };
  }
  const wide = findHeader(rows, [["CODE", "DBS"], ["DESCRIPTION", "TITLE"]]);
  if (!wide) return { entries: [], errors: [{ line: 1, message: "Couldn't find Period/DBS/Hours or Code/Description headers" }] };
  const head = rows[wide.row] ?? [];
  const dateCols: { col: number; period: number }[] = [];
  head.forEach((c, i) => {
    const d = c instanceof Date || typeof c === "string" ? parseDate(c) : null;
    if (!d || i === wide.col(0) || i === wide.col(1)) return;
    const p = periods.find((x) => x.cutoff === d);
    if (!p) errors.push({ line: wide.row + 1, message: `Column date ${d} is not a cutoff in the calendar` });
    else dateCols.push({ col: i, period: p.number });
  });
  const entries: ActualRow[] = [];
  for (let r = wide.row + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const dbs = str(row[wide.col(0)]);
    if (!dbs) continue;
    for (const dc of dateCols) {
      const hrs = num(row[dc.col]);
      if (hrs === null || hrs === 0) continue;
      entries.push({ periodNumber: dc.period, codes: { dbs }, hours: hrs, indirect: false });
    }
  }
  return { entries, errors };
}

export function exportActuals(entries: ActualRow[]): OutSheet {
  return {
    name: "Actual Hours",
    headerRow: 0,
    rows: [
      ["Period", "DBS", "WBS", "OBS", "PBS", "Hours", "Indirect"],
      ...entries.map((e) => [e.periodNumber, e.codes.dbs ?? null, e.codes.wbs ?? null, e.codes.obs ?? null, e.codes.pbs ?? null, e.hours, e.indirect ? "Y" : null]),
    ],
  };
}

// ---------- Deliverables (E09 initial load, E10 day-to-day) ----------

export type StepField = "milestone" | "pbs" | "obs" | "wbs" | "sbs" | "planned" | "forecast" | "earned" | "planningRef";
const STEP_HEADERS: [RegExp, StepField][] = [
  [/^MILESTONE\s*(\d+)$/, "milestone"],
  [/^PBS\s*(\d+)$/, "pbs"],
  [/^OBS\s*(\d+)$/, "obs"],
  [/^WBS\s*(\d+)$/, "wbs"],
  [/^SBS\s*(\d+)$/, "sbs"],
  [/^(?:INITIAL DATE|INITIAL PLANNED DATE|PLANNED DATE|PLAN DATE)\s*(\d+)$/, "planned"],
  [/^FORECAST DATE\s*(\d+)$/, "forecast"],
  [/^(?:ACTUAL DATE|EARNED DATE)\s*(\d+)$/, "earned"],
  [/^(?:PLANNING SYSTEM SUPPORT IMPORT|PLANNING SYSTEM IMPORT DATE|PLANNING SYSTEM IMPORT|P6 ID)\s*(\d+)$/, "planningRef"],
];
const DLV_FIELDS: Record<string, string[]> = {
  code: ["DLV REFERENCE", "DLV REF", "DELIVERABLE", "REFERENCE"],
  category: ["DLV CATEGORY", "CATEGORY"],
  title: ["DLV TITLE", "TITLE"],
  clientRef: ["CLIENT REFERENCE"],
  free1: ["FREE CRITERIA1"],
  free2: ["FREE CRITERIA2"],
  free3: ["FREE CRITERIA3"],
  free4: ["FREE CRITERIA4"],
  dbs: ["DBS"],
  wbs: ["WBS"],
  obs: ["OBS"],
  pbs: ["PBS"],
  cbs: ["CBS"],
  sbs: ["SBS", "XBS", "SBS/XBS"],
  weight: ["WEIGHT", "WEIGHT HRS", "WEIGHT HOURS"],
  weightManuf: ["WEIGHT MANUF", "WEIGHT $"],
  unit: ["UNIT"],
  docType: ["DOC TYPE", "DOCUMENT TYPE"],
  chainCode: ["MILESTONE CHAIN", "MS CHAIN", "CHAIN"],
};

export interface ParsedDeliverable {
  line: number;
  /** Only the fields present in the file; absent columns keep their current values on update. */
  fields: Partial<Record<keyof typeof DLV_FIELDS, string | number | null>>;
  steps: Record<number, Partial<Record<StepField, string | null>>>;
  errors: string[];
}

export function parseDeliverables(rows: Cell[][]): { items: ParsedDeliverable[]; errors: RowError[]; columns: string[] } {
  const hr = rows.findIndex((r, i) => i < 30 && (r ?? []).map(norm).some((c) => DLV_FIELDS.code.includes(c)));
  if (hr < 0) return { items: [], errors: [{ line: 1, message: "Couldn't find the DLV REFERENCE header" }], columns: [] };
  const head = (rows[hr] ?? []).map(norm);
  const fieldCol: Partial<Record<string, number>> = {};
  const stepCols: { col: number; n: number; field: StepField }[] = [];
  const taken = new Set<string>();
  head.forEach((h, i) => {
    if (!h || taken.has(h)) return; // first occurrence wins (his files repeat names in side areas)
    for (const [re, f] of STEP_HEADERS) {
      const m = h.match(re);
      if (m) {
        stepCols.push({ col: i, n: Number(m[1]), field: f });
        taken.add(h);
        return;
      }
    }
    for (const [f, aliases] of Object.entries(DLV_FIELDS)) {
      if (fieldCol[f] === undefined && aliases.includes(h)) {
        fieldCol[f] = i;
        taken.add(h);
        return;
      }
    }
  });
  const items: ParsedDeliverable[] = [];
  const errors: RowError[] = [];
  for (let r = hr + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const code = str(row[fieldCol.code!]);
    if (!code) {
      if (row.some((c) => str(c))) errors.push({ line: r + 1, message: "Row has data but no DLV REFERENCE; skipped" });
      continue;
    }
    const it: ParsedDeliverable = { line: r + 1, fields: {}, steps: {}, errors: [] };
    for (const [f, c] of Object.entries(fieldCol)) {
      if (c === undefined) continue;
      const v = row[c];
      if (f === "weight" || f === "weightManuf") {
        const n = num(v);
        if (str(v) && n === null) it.errors.push(`${f === "weight" ? "WEIGHT" : "WEIGHT MANUF"} "${str(v)}" is not a number`);
        it.fields[f as keyof typeof DLV_FIELDS] = n;
      } else it.fields[f as keyof typeof DLV_FIELDS] = str(v) || null;
    }
    for (const sc of stepCols) {
      const v = row[sc.col];
      const s = (it.steps[sc.n] ??= {});
      if (sc.field === "planned" || sc.field === "forecast" || sc.field === "earned") {
        const d = parseDate(v);
        if (str(v) && !d) it.errors.push(`${sc.field} date ${sc.n} "${str(v)}" is not a date`);
        s[sc.field] = d;
      } else s[sc.field] = str(v) ? (sc.field === "milestone" ? str(v).toUpperCase() : str(v)) : null;
    }
    items.push(it);
  }
  return { items, errors, columns: head.filter(Boolean) };
}

export interface ExportDeliverable {
  code: string;
  title?: string;
  category?: string;
  clientRef?: string;
  free?: string[];
  docType?: string;
  codes: Codes;
  weight: number;
  weightManuf?: number;
  unit?: "h" | "$";
  chainCode: string;
  steps: { milestone: string; planned?: string | null; forecast?: string | null; earned?: string | null; codes?: Codes; planningRef?: string }[];
}

/** One layout for both initial load and day-to-day updates: Cesar's LISTDLV columns plus P/F/Earned dates per step. */
export function exportDeliverables(projectCode: string, ds: ExportDeliverable[]): OutSheet {
  const max = Math.max(1, ...ds.map((d) => d.steps.length));
  const head: Cell[] = ["DLV REFERENCE", "DLV CATEGORY", "DLV TITLE", "CLIENT REFERENCE", "FREE CRITERIA1", "FREE CRITERIA2", "FREE CRITERIA3", "FREE CRITERIA4", "DOC TYPE", "DBS", "WBS", "OBS", "PBS", "CBS", "SBS", "WEIGHT", "WEIGHT MANUF", "UNIT", "MILESTONE CHAIN"];
  for (let n = 1; n <= max; n++)
    head.push(`MILESTONE${n}`, `PBS${n}`, `OBS${n}`, `WBS${n}`, `SBS${n}`, `PLANNED DATE${n}`, `FORECAST DATE${n}`, `EARNED DATE${n}`, `PLANNING SYSTEM SUPPORT IMPORT${n}`);
  const rows: Cell[][] = [["Project Code :", projectCode], ["Export from / Import through :", "EVM"], head];
  for (const d of ds) {
    const r: Cell[] = [d.code, d.category ?? null, d.title ?? null, d.clientRef ?? null, d.free?.[0] ?? null, d.free?.[1] ?? null, d.free?.[2] ?? null, d.free?.[3] ?? null, d.docType ?? null, d.codes.dbs ?? null, d.codes.wbs ?? null, d.codes.obs ?? null, d.codes.pbs ?? null, d.codes.cbs ?? null, d.codes.sbs ?? null, d.weight, d.weightManuf ?? null, d.unit === "$" ? "$" : "h", d.chainCode];
    for (let n = 0; n < max; n++) {
      const s = d.steps[n];
      r.push(s?.milestone ?? null, s?.codes?.pbs ?? null, s?.codes?.obs ?? null, s?.codes?.wbs ?? null, s?.codes?.sbs ?? null, xlDate(s?.planned), xlDate(s?.forecast), xlDate(s?.earned), s?.planningRef ?? null);
    }
    rows.push(r);
  }
  return { name: "LISTDLV", rows, headerRow: 2 };
}

// ---------- Resolution: parsed rows -> full rows + preview (import preview before commit) ----------

export interface ExistingDeliverable extends ExportDeliverable {
  _id: string;
}

export interface ResolvedRow {
  line: number;
  code: string;
  status: "new" | "changed" | "unchanged" | "error";
  errors: string[];
  changes: string[];
  row?: ExportDeliverable;
}

export function resolveDeliverables(
  parsed: ParsedDeliverable[],
  existing: ExistingDeliverable[],
  chains: Map<string, Chain>,
): ResolvedRow[] {
  const byCode = new Map(existing.map((d) => [d.code, d]));
  const seen = new Map<string, number>();
  return parsed.map((p) => {
    const f = p.fields;
    const code = String(f.code ?? "").trim();
    const errors = [...p.errors];
    if (seen.has(code)) errors.push(`Appears twice (also row ${seen.get(code)})`);
    seen.set(code, p.line);
    const ex = byCode.get(code);
    const has = (k: keyof typeof DLV_FIELDS) => k in f;
    const pick = <T,>(k: keyof typeof DLV_FIELDS, cur: T | undefined): T | undefined => (has(k) ? ((f[k] ?? undefined) as T | undefined) : cur);

    let chainCode = String(pick<string>("chainCode", ex?.chainCode) ?? "").toUpperCase();
    const fileMs = Object.entries(p.steps)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([, s]) => s.milestone)
      .filter((m): m is string => !!m);
    if (!chainCode && fileMs.length) {
      // No chain column: accept only an exact, unique match on the milestone sequence.
      const matches = [...chains.values()].filter((c) => c.steps.length === fileMs.length && c.steps.every((s, i) => s.milestone === fileMs[i]));
      if (matches.length === 1) chainCode = matches[0].code;
      else errors.push(matches.length ? `Milestones match several chains (${matches.map((m) => m.code).join(", ")}); add a MILESTONE CHAIN column` : `No chain has milestones ${fileMs.join(" / ")}; add a MILESTONE CHAIN column`);
    }
    const chain = chainCode ? chains.get(chainCode) : undefined;
    if (chainCode && !chain) errors.push(`Chain ${chainCode} does not exist`);
    const weight = pick<number>("weight", ex?.weight);
    if (weight === undefined || weight === null) errors.push("WEIGHT is required");

    const codes: Codes = { ...(ex?.codes ?? {}) };
    for (const b of BREAKDOWNS) if (has(b as keyof typeof DLV_FIELDS)) {
      const v = f[b as keyof typeof DLV_FIELDS];
      if (v) codes[b as Breakdown] = String(v);
      else delete codes[b as Breakdown];
    }
    let steps: ExportDeliverable["steps"] = [];
    if (chain) {
      const exByMs = new Map((ex?.chainCode === chain.code ? ex.steps : []).map((s) => [s.milestone, s]));
      steps = chain.steps.map((cs, i) => {
        const cur = exByMs.get(cs.milestone) ?? { milestone: cs.milestone };
        const fs = p.steps[i + 1] ?? {};
        if (fs.milestone && fs.milestone !== cs.milestone) errors.push(`MILESTONE${i + 1} is ${fs.milestone} but chain ${chain.code} step ${i + 1} is ${cs.milestone}`);
        const stepCodes: Codes = { ...(cur.codes ?? {}) };
        for (const b of ["pbs", "obs", "wbs", "sbs"] as const) if (b in fs) {
          if (fs[b]) stepCodes[b] = fs[b]!;
          else delete stepCodes[b];
        }
        const out = { ...cur, milestone: cs.milestone, codes: stepCodes } as ExportDeliverable["steps"][number];
        for (const k of ["planned", "forecast", "earned"] as const) if (k in fs) out[k] = fs[k] ?? null;
        if ("planningRef" in fs) out.planningRef = fs.planningRef ?? undefined;
        return out;
      });
      const extra = Object.keys(p.steps).map(Number).filter((n) => n > chain.steps.length && Object.values(p.steps[n]).some(Boolean));
      if (extra.length) errors.push(`Has data in step ${extra.join(", ")} but chain ${chain.code} has ${chain.steps.length} steps`);
    }
    const free = has("free1") || has("free2") || has("free3") || has("free4")
      ? [1, 2, 3, 4].map((i) => String(f[`free${i}` as keyof typeof DLV_FIELDS] ?? ""))
      : ex?.free;
    const unitRaw = pick<string>("unit", ex?.unit);
    const row: ExportDeliverable = {
      code,
      title: pick<string>("title", ex?.title),
      category: pick<string>("category", ex?.category),
      clientRef: pick<string>("clientRef", ex?.clientRef),
      docType: pick<string>("docType", ex?.docType),
      free,
      codes,
      weight: Number(weight ?? 0),
      weightManuf: pick<number>("weightManuf", ex?.weightManuf),
      unit: unitRaw === "$" ? "$" : "h",
      chainCode,
      steps,
    };
    if (errors.length) return { line: p.line, code, status: "error", errors, changes: [] };
    if (!ex) return { line: p.line, code, status: "new", errors, changes: [], row };
    const changes = diffDeliverable(ex, row);
    return { line: p.line, code, status: changes.length ? "changed" : "unchanged", errors, changes, row };
  });
}

function diffDeliverable(a: ExportDeliverable, b: ExportDeliverable): string[] {
  const out: string[] = [];
  const cmp = (label: string, x: unknown, y: unknown) => {
    const nx = x ?? "";
    const ny = y ?? "";
    if (JSON.stringify(nx) !== JSON.stringify(ny)) out.push(`${label}: ${fmt(nx)} → ${fmt(ny)}`);
  };
  cmp("Title", a.title, b.title);
  cmp("Weight", a.weight, b.weight);
  cmp("Chain", a.chainCode, b.chainCode);
  for (const k of BREAKDOWNS) cmp(k.toUpperCase(), a.codes[k], b.codes[k]);
  cmp("Category", a.category, b.category);
  cmp("Doc type", a.docType, b.docType);
  b.steps.forEach((s, i) => {
    const o = a.steps[i] ?? { milestone: s.milestone };
    for (const k of ["planned", "forecast", "earned"] as const) cmp(`${s.milestone} ${k}`, o[k], s[k]);
    for (const k of ["pbs", "obs", "wbs", "sbs"] as const) cmp(`${s.milestone} ${k.toUpperCase()}`, o.codes?.[k], s.codes?.[k]);
    cmp(`${s.milestone} P6 ref`, o.planningRef, s.planningRef);
  });
  return out;
}

const fmt = (v: unknown) => (v === "" ? "(blank)" : String(v));
